import { Card, Table, Tag, Typography } from 'antd';
import type { BdoSummaryResponse } from '../types';
import { formatMg } from '../types';

const { Text } = Typography;

interface Props {
    summary?: BdoSummaryResponse;
    loading?: boolean;
    isMobile?: boolean;
}

export default function BdoTable51({ summary, loading, isMobile }: Props) {
    const rows = summary?.materials || [];

    const columns = [
        {
            title: 'Materiał',
            dataIndex: 'label',
            key: 'label',
            render: (label: string, record: any) => (
                <Text strong={record.material === 'OTHER'}>{label}</Text>
            ),
        },
        {
            title: 'Produkty [Mg]',
            dataIndex: 'productMg',
            key: 'productMg',
            align: 'right' as const,
            render: (v: number) => formatMg(v),
        },
        {
            title: 'Opakowania zbiorcze [Mg]',
            dataIndex: 'collectiveMg',
            key: 'collectiveMg',
            align: 'right' as const,
            responsive: isMobile ? undefined : (['md'] as any),
            render: (v: number) => formatMg(v),
        },
        {
            title: 'Razem [Mg]',
            dataIndex: 'totalMg',
            key: 'totalMg',
            align: 'right' as const,
            render: (v: number) => <Text strong>{formatMg(v)}</Text>,
        },
        {
            title: 'Udział %',
            dataIndex: 'sharePercent',
            key: 'sharePercent',
            align: 'right' as const,
            render: (v: number) => `${Number(v || 0).toFixed(1)}%`,
        },
        {
            title: 'Wiersz',
            dataIndex: 'bdoRow',
            key: 'bdoRow',
            render: (v: string) =>
                v === '1A (Pozostałe)' ? (
                    <Tag color="default">{v}</Tag>
                ) : (
                    <Tag color="blue">1B</Tag>
                ),
        },
    ];

    const totals = summary?.totals;

    return (
        <Card
            size="small"
            title="Tabela 5.1 — opakowania ogółem"
            extra={<Text type="secondary">1A / 1B</Text>}
        >
            <Table
                dataSource={rows}
                columns={columns}
                rowKey="material"
                loading={loading}
                pagination={false}
                size={isMobile ? 'small' : 'middle'}
                scroll={{ x: isMobile ? 520 : undefined }}
                summary={() => (
                    <>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                                <Text strong>RAZEM 1A</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                                <Text strong>{formatMg(totals?.productsMg)}</Text>
                            </Table.Summary.Cell>
                            {!isMobile && (
                                <Table.Summary.Cell index={2} align="right">
                                    <Text strong>{formatMg(totals?.collectiveMg)}</Text>
                                </Table.Summary.Cell>
                            )}
                            <Table.Summary.Cell index={3} align="right">
                                <Text strong>{formatMg(totals?.total1AMg)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">
                                <Text strong>100.0%</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5}>
                                <Tag color="geekblue">1A</Tag>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                        {(totals?.multimaterialMg || 0) > 0 && (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>
                                    <Text italic>Wielomateriałowe (5.2)</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    {formatMg(summary?.multimaterial.productMg)}
                                </Table.Summary.Cell>
                                {!isMobile && (
                                    <Table.Summary.Cell index={2} align="right">
                                        {formatMg(summary?.multimaterial.collectiveMg)}
                                    </Table.Summary.Cell>
                                )}
                                <Table.Summary.Cell index={3} align="right">
                                    <Text strong>{formatMg(summary?.multimaterial.totalMg)}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right">
                                    —
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>
                                    <Tag color="orange">5.2</Tag>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        )}
                    </>
                )}
            />
            {isMobile && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Kolumna „Opakowania zbiorcze” jest ukryta na telefonie — sumy Razem
                    zawierają oba źródła.
                </Text>
            )}
        </Card>
    );
}
